import { HabitSchedule, Timestamp, WeekDays } from '@griply/types';
import { isAfter, isBefore, isSameDay } from 'date-fns';

import { createPlannedOccurrenceForDate } from './create-planned-occurrence-for-date';
import { deletePlannedOccurrenceForDate } from './delete-planned-occurrence-for-date';
import { getScheduleByDate } from './get-schedule-by-date';
import { getScheduleEndDate } from './get-schedule-end-date';

export type UpdateWeekOccurrencesOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const updateWeekOccurrences = (
  schedules: HabitSchedule[],
  currentDates: Timestamp[],
  selectedDates: Timestamp[],
  periodDate: Timestamp,
  { today, weekStartsOn }: UpdateWeekOccurrencesOptions,
): HabitSchedule[] => {
  // Find dates to add (in new but not in current)
  const datesToAdd = selectedDates.filter(
    (newDate) =>
      !currentDates.some((currentDate) => isSameDay(currentDate, newDate)),
  );

  // Find dates to remove (in current but not in new)
  const datesToRemove = currentDates.filter(
    (currentDate) =>
      !selectedDates.some((newDate) => isSameDay(newDate, currentDate)),
  );

  let updatedSchedules = schedules;
  const scheduleForPeriod = getScheduleByDate(updatedSchedules, {
    date: periodDate,
    today,
  }); // Use a representative schedule

  // Add new occurrences
  datesToAdd.forEach((date) => {
    updatedSchedules = createPlannedOccurrenceForDate(updatedSchedules, date, {
      timeslot: scheduleForPeriod?.timeslot,
      today,
      weekStartsOn,
    });
  });

  // Remove unselected occurrences
  datesToRemove.forEach((date) => {
    updatedSchedules = updatedSchedules
      .toSorted(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .map((schedule, index, arr) => {
        const prevSchedule = arr[index - 1];
        const prevScheduleEndDate = prevSchedule
          ? getScheduleEndDate(prevSchedule)
          : undefined;
        const scheduleEndDate = getScheduleEndDate(schedule);

        if (
          (prevScheduleEndDate && isBefore(date, prevScheduleEndDate)) ||
          (scheduleEndDate && isAfter(date, scheduleEndDate))
        ) {
          return schedule;
        }

        return deletePlannedOccurrenceForDate(schedule, date, {
          weekStartsOn,
          today,
        });
      });
  });

  return updatedSchedules;
};
